import { Button, Icon, Image, NameAvatar, Typography } from '@smartgift/frontend-toolkit';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { SkeletonLoaders } from '@hero/components';
import { PageLayout } from '@hero/components/layouts';
import { DownloadReport, ThankYouDetailModal } from '@hero/components/modals';
import { ChooseFeatureTypeModal } from '@hero/components/organisms';
import { configs } from '@hero/configs';
import { PRODUCT_TYPES } from '@hero/constants';
import { useInViewObserver } from '@hero/hooks';
import { ROUTE_PATHS } from '@hero/routes';
import { useThankYouMessagesReport } from '@hero/services/analytic-service/hooks';
import { useGetGratitudes } from '@hero/services/order-service/hook';
import { convertHtmlMessageToString } from '@hero/utils';
import { formatTimeAgo } from '@hero/utils/dateUtils';

import * as S from './style';

const ITEMS_PER_PAGE = 30;

export default function Page() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const loadMoreRef = useRef(null);
  const [isSendGiftModalOpen, setIsSendGiftModalOpen] = useState(false);
  const [selectedGratitude, setSelectedGratitude] = useState(null);

  const {
    data: gratitudes,
    isLoading,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage
  } = useGetGratitudes({
    pagination: {
      limit: ITEMS_PER_PAGE
    },
    filters: {
      associationType: 'recognition'
    }
  });

  useInViewObserver(loadMoreRef, () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  });

  const allGratitudes = gratitudes?.pages?.flatMap(page => page.docs) || [];
  const totalDocs = gratitudes?.pages?.[0]?.totalDocs || 0;

  const { mutateAsync: generateTYMessagesReport } = useThankYouMessagesReport();
  const [isOpenDownloadReport, setIsOpenDownloadReport] = useState(false);

  const openDownloadReportHandler = () => {
    setIsOpenDownloadReport(true);
  };
  const closeDownloadReportHandler = () => {
    setIsOpenDownloadReport(false);
  };

  function handleCreate() {
    if (configs?.general?.simplifyFlow) {
      navigate(ROUTE_PATHS.SEND_GIFT_OCCASIONS);
    } else {
      setIsSendGiftModalOpen(true);
    }
  }

  return (
    <PageLayout
      header={{
        backButton: true,
        title: t('gratitudeBoard'),
        components: {
          EndContent: () => (
            <Button
              style={{
                textTransform: 'uppercase',
                textDecoration: 'none',
                fontWeight: 700
              }}
              variant="link"
              color="primary"
              startIcon={<Icon name="email" size="md" />}
              onClick={openDownloadReportHandler}
            >
              {t('getSpreadsheet')}
            </Button>
          )
        },
        showSalesNumber: false
      }}
    >
      <DownloadReport
        isOpen={isOpenDownloadReport}
        onRequestClose={closeDownloadReportHandler}
        onAsyncSubmit={async data => {
          await generateTYMessagesReport({
            organizationCode: data?.organization?.code,
            emails: data.contacts.map(item => item.email)
          });
        }}
      />
      <S.GratitudeBoard>
        {!isLoading && (
          <S.GratitudeBoardInfo>
            <Typography variant="body1Bold">{t('thankYouS')}</Typography>
            <Typography variant="body2" emphasis="weak">
              ({totalDocs} total)
            </Typography>
          </S.GratitudeBoardInfo>
        )}

        {!isLoading && totalDocs === 0 && (
          <S.GratitudeBoardEmpty>
            <Icon name="gift-box" size="xxl" color="var(--color-success-default)" />
            <Typography variant="body2SemiBold">{t('noGratitudes')}</Typography>
            <Button onClick={handleCreate}>{t('sendGifts')}</Button>
          </S.GratitudeBoardEmpty>
        )}

        <ChooseFeatureTypeModal
          isOpen={isSendGiftModalOpen}
          onClose={() => {
            setIsSendGiftModalOpen(false);
          }}
        />

        {selectedGratitude && (
          <ThankYouDetailModal
            isOpen={!!selectedGratitude}
            onRequestClose={() => setSelectedGratitude(null)}
            selectedGratitude={selectedGratitude}
          />
        )}

        <S.GratitudeBoardCards>
          {isLoading &&
            Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
              <SkeletonLoaders.GratitudeBoardItem key={index} />
            ))}

          {!isLoading &&
            allGratitudes.map(item => (
              <S.GratitudeBoardCard
                hasImagePadding={item?.selectedSku?.meta?.productType === PRODUCT_TYPES.GIFT_CARD}
                key={item.id}
                onClick={() => setSelectedGratitude(item)}
              >
                <Image
                  src={item?.selectedSku?.images?.find(image => image.default)?.url}
                  alt={item.message}
                  width="100%"
                  height="auto"
                />
                <S.GratitudeBoardCardInfo>
                  <S.GratitudeBoardCardUser>
                    <NameAvatar
                      name={`${item.recipient.firstName} ${item.recipient.lastName}`}
                      rounded
                      size={32}
                    />
                    <Typography variant="body2Bold">{item.recipient.firstName}</Typography>
                  </S.GratitudeBoardCardUser>
                  <Typography variant="caption1" emphasis="weak">
                    {formatTimeAgo(item?.status?.acceptedAt)}
                  </Typography>
                </S.GratitudeBoardCardInfo>

                <S.GratitudeBoardCardMessage>
                  <Typography variant="body2" emphasis="weak">
                    {convertHtmlMessageToString(item?.experience?.thankYouMessage)}
                  </Typography>
                </S.GratitudeBoardCardMessage>
              </S.GratitudeBoardCard>
            ))}

          {isFetchingNextPage &&
            Array.from({ length: ITEMS_PER_PAGE }).map((_, index) => (
              <SkeletonLoaders.GratitudeBoardItem key={`next-${index}`} />
            ))}

          {hasNextPage && <div ref={loadMoreRef} style={{ height: '20px' }} />}
        </S.GratitudeBoardCards>
      </S.GratitudeBoard>
    </PageLayout>
  );
}
